import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Flex, Modal } from "@mantine/core";
import styled from "styled-components";
import { Link } from "react-router";
import DataTable, { Column } from "../common/Table/DataTable";
import WeeklyShiftTable from "./WeeklyShiftTable";
import CustomSelect from "../common/Inputs/CustomSelect";
import CustomDatePicker from "../common/Inputs/CustomDatePicker";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import ShiftConflictErrorModal from "../common/Modals/ShiftConflictErrorModal";
import {
  AssignmentConflict,
  assignEmployeesToShift,
  deleteShiftAssignment,
  getEmployeeShiftAssignments,
  getShiftDefinitionById,
  getShiftDefinitionSelectData,
} from "../../api/shiftDefinitionApi";
import {
  getRotationPreview,
  getShiftRotations,
} from "../../api/shiftRotationApi";
import { createNotification } from "../../utils/useNotification";
import {
  dayRangeLabel,
  formatDayKey,
  todayKey,
  toDayKey,
  weeklyWorkHours,
} from "../../utils/shiftPattern";
import { DAYS_OF_WEEK } from "../../utils/shiftPattern";

/**
 * Personelin ÇALIŞMA DÜZENİ sekmesi (plan 2.24) — canlı `/shift-assignment`
 * uçlarından okur. Eskiden backend'de hiç var olmayan `/employee/activeShifts`
 * ucuna bakıyordu ve ekran tamamen yorum satırındaydı.
 *
 * "Bugünkü düzen" ayrı gösteriliyor çünkü atamalar tarih aralıklı: kişinin
 * vardiyası sabit bir özellik değil, tarihe göre değişiyor (dönüşümlü vardiya).
 */
const EmployeeShiftDetails = ({ employeeId }: { employeeId: string }) => {
  const [assignments, setAssignments] = useState<any[]>([]);
  const [current, setCurrent] = useState<any | null>(null);
  const [shiftOptions, setShiftOptions] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedShift, setSelectedShift] = useState<string | null>(null);
  // Mantine 8'de tarih seçicilerin değeri ZATEN "YYYY-MM-DD" string.
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [conflicts, setConflicts] = useState<AssignmentConflict[]>([]);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);

  /**
   * Ekip atamasında bu DÖNEMİN vardiyası — rotadan çözülür.
   * `null` = ekip bu dönem dinleniyor. `undefined` = henüz çözülmedi / rota yok.
   */
  const [rotationShift, setRotationShift] = useState<any | null | undefined>(
    undefined
  );
  const [rotationInfo, setRotationInfo] = useState<{
    ad: string;
    donemBaslangic: string;
    donemBitis: string;
  } | null>(null);
  const [rotationLoading, setRotationLoading] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId) return;
    const today = todayKey();
    try {
      const [all, onToday]: any[] = await Promise.all([
        getEmployeeShiftAssignments(employeeId),
        getEmployeeShiftAssignments(employeeId, today),
      ]);
      setAssignments(all?.data ?? []);
      setCurrent(onToday?.data?.[0] ?? null);
    } catch {
      setAssignments([]);
      setCurrent(null);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * EKİP → ROTA → DÖNEM → VARDİYA.
   *
   * Ekip atamasında `shiftDefinitionId` YOKTUR: kişinin o haftaki vardiyası
   * saklanmıyor, ekibin rotasından hesaplanıyor (plan 2.33/2.34). Bu sekme
   * eskiden bunu çözmediği için "önce bir vardiya atanmalı" diyordu — oysa
   * vardiya vardı ve İK ikinci bir atama yapmaya itiliyordu; o atama da ekip
   * atamasının üstüne biniyor.
   *
   * Zincir:
   *   1. `shiftTeamId` hangi rotanın `teamIds` dizisinde? (rota listesi)
   *   2. O rotanın BUGÜNDEN itibaren 1 dönemlik önizlemesi (matris ucu —
   *      "Bu Hafta Kim Nerede" ekranıyla aynı gövde, ayrı matematik yok)
   *   3. Matriste bu ekibin hücresi → `shiftDefinitionId` (null = dinlenme)
   *   4. Haftalık tabloyu çizebilmek için vardiyanın `days` alanı gerekiyor;
   *      hücre yalnızca kod/ad taşıdığı için tanım ayrıca çekiliyor.
   */
  useEffect(() => {
    const teamId = current?.shiftTeamId;
    if (!teamId) {
      setRotationShift(undefined);
      setRotationInfo(null);
      return;
    }

    let iptal = false;
    const coz = async () => {
      setRotationLoading(true);
      try {
        const rotations: any = await getShiftRotations({ limit: 100 });
        const rota = (rotations?.data ?? []).find((r: any) =>
          (r.teamIds ?? []).some((t: any) => String(t) === String(teamId))
        );
        if (!rota) {
          if (!iptal) { setRotationShift(undefined); setRotationInfo(null); }
          return;
        }

        const onizleme: any = await getRotationPreview(rota._id, todayKey(), 1);
        const donem = onizleme?.data?.periods?.[0];
        const hucre = (donem?.cells ?? []).find(
          (c: any) => String(c.teamId) === String(teamId)
        );

        if (iptal) return;
        setRotationInfo({
          ad: rota.name,
          donemBaslangic: donem?.startDate,
          donemBitis: donem?.endDate,
        });

        // Hücre yok ya da dinlenme dönemi → null ("dinleniyor")
        if (!hucre || !hucre.shiftDefinitionId) {
          setRotationShift(null);
          return;
        }

        /*
         * ⚠ Tekil kayıt uçları `response` döner, liste uçları `data`. İkisini
         * karıştırmak sessiz bir hataya yol açıyor: tanım boş gelince ekran
         * "bu dönem dinleniyor" diyordu — oysa vardiya vardı, sadece
         * okunamamıştı. Bulunamazsa `undefined` bırakılır (dinlenme İDDİA
         * EDİLMEZ), aşağıdaki genel not gösterilir.
         */
        const tanim: any = await getShiftDefinitionById(hucre.shiftDefinitionId);
        const kayit = tanim?.response ?? tanim?.data;
        if (!iptal) setRotationShift(kayit ?? undefined);
      } catch {
        if (!iptal) { setRotationShift(undefined); setRotationInfo(null); }
      } finally {
        if (!iptal) setRotationLoading(false);
      }
    };

    coz();
    return () => {
      iptal = true;
    };
  }, [current]);

  useEffect(() => {
    if (!modalOpen) return;
    setSelectedShift(null);
    setStartDate(null);
    setEndDate(null);
    getShiftDefinitionSelectData()
      .then((data: any) => setShiftOptions(data?.data ?? []))
      .catch(() => setShiftOptions([]));
  }, [modalOpen]);

  const submit = async (closePrevious: boolean) => {
    const startDayKey = toDayKey(startDate);
    if (!selectedShift || !startDayKey) {
      createNotification({
        success: false,
        message: "Vardiya ve başlangıç tarihi seçin.",
      });
      return;
    }

    setSaving(true);
    try {
      const response: any = await assignEmployeesToShift({
        shiftDefinitionId: selectedShift,
        employeeIds: [employeeId],
        startDate: startDayKey,
        endDate: toDayKey(endDate),
        closePrevious,
      });

      if (response?.success) {
        createNotification({ success: true, message: response.message });
        setModalOpen(false);
        await load();
        return;
      }
      if (response?.conflicts?.length) {
        setConflicts(response.conflicts);
        setConflictModalOpen(true);
        return;
      }
      createNotification({
        success: false,
        message: response?.message ?? "Atama yapılamadı.",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (assignmentId: string) => {
    const response: any = await deleteShiftAssignment(assignmentId);
    createNotification({
      success: response?.success,
      message: response?.success ? "Atama kaldırıldı." : response?.message,
    });
    if (response?.success) await load();
  };

  const currentDefinition = current?.definition;
  /*
   * Ekip ataması (plan 2.33): `definition` boştur, çünkü o günkü vardiya
   * SAKLANMIYOR — ekibin rotasından hesaplanıyor. Bu sekme atamayı
   * gösterdiği için ekibi göstermek doğru olan; hangi vardiyaya denk geldiğini
   * görmek "Bu Hafta Kim Nerede" ekranının işi.
   */
  const currentTeam = current?.team;

  /*
   * Sayfalaması yok — `DataTable`'ın sayfalama prop'ları bu yüzden isteğe bağlı
   * yapıldı. Kip (modal) içinde de olsa telefonda kart kipinden yararlanıyor:
   * 4 kolon dar kipte yan yana okunmuyordu.
   */
  const assignmentColumns: Column[] = [
    {
      key: "shift",
      header: "Vardiya / Ekip",
      render: (row: any) =>
        row.team ? (
          <Badge variant="light" color="grape">
            {row.team.code} · {row.team.name}
          </Badge>
        ) : row.definition ? (
          `${row.definition.code} · ${row.definition.name}`
        ) : (
          "—"
        ),
    },
    {
      key: "startDate",
      header: "Başlangıç",
      render: (row: any) => formatDayKey(row.startDate),
    },
    {
      key: "endDate",
      header: "Bitiş",
      render: (row: any) => (row.endDate ? formatDayKey(row.endDate) : "Süresiz"),
    },
    {
      key: "actions",
      header: "",
      headerAlign: "right",
      render: (row: any) => (
        <Flex justify="flex-end">
          <TableDeleteButton onClick={() => remove(row._id)} />
        </Flex>
      ),
    },
  ];

  return (
    <div>
      <SectionTitle>Bugünkü Çalışma Düzeni</SectionTitle>
      {currentTeam ? (
        <Flex align="center" gap={12} mb={8} wrap="wrap">
          <Badge variant="light" color="grape" size="lg">
            {currentTeam.code} · {currentTeam.name}
          </Badge>
          <Muted>
            {formatDayKey(current.startDate)} –{" "}
            {current.endDate ? formatDayKey(current.endDate) : "süresiz"} ·
            vardiyası rotadan hesaplanıyor
          </Muted>
        </Flex>
      ) : currentDefinition ? (
        <Flex align="center" gap={12} mb={8}>
          <Badge variant="light" color="indigo" size="lg">
            {currentDefinition.code} · {currentDefinition.name}
          </Badge>
          <Muted>
            {formatDayKey(current.startDate)} –{" "}
            {current.endDate ? formatDayKey(current.endDate) : "süresiz"}
            {currentDefinition.dayCutoff
              ? ` · gün kesimi ${currentDefinition.dayCutoff}`
              : ""}
          </Muted>
        </Flex>
      ) : (
        <Muted>Bugün için tanımlı bir çalışma düzeni yok.</Muted>
      )}

      <Button
        variant="outline"
        mt={16}
        mb={36}
        color="#5C26FF"
        radius="8px"
        onClick={() => setModalOpen(true)}
      >
        Vardiya Ata / Kaldır
      </Button>

      <SectionTitle>Haftalık Programı</SectionTitle>
      {currentDefinition ? (
        <>
          <Muted style={{ marginBottom: 12 }}>
            Haftalık toplam {weeklyWorkHours(currentDefinition.days)} saat
          </Muted>
          <WeeklyShiftTable
            days={currentDefinition.days}
            shiftName={currentDefinition.code}
            color={currentDefinition.color}
          />
          <DayList>
            {DAYS_OF_WEEK.map((d) => {
              const day = currentDefinition.days?.find(
                (x: any) => x.dayOfWeek === d.value
              );
              return (
                <DayRow key={d.value}>
                  <DayName>{d.label}</DayName>
                  <span>{day ? dayRangeLabel(day) : "Çalışılmıyor"}</span>
                </DayRow>
              );
            })}
          </DayList>
        </>
      ) : currentTeam && rotationShift ? (
        /* Ekip ataması + rotadan çözülen vardiya: haftalık program artık ÇİZİLİYOR. */
        <>
          <Muted style={{ marginBottom: 12 }}>
            <strong>{currentTeam.code} · {currentTeam.name}</strong> ekibi bu
            dönem <strong>{rotationShift.code} · {rotationShift.name}</strong>{" "}
            vardiyasında · haftalık toplam{" "}
            {weeklyWorkHours(rotationShift.days)} saat
            {rotationInfo ? (
              <>
                {" "}· dönem {formatDayKey(rotationInfo.donemBaslangic)} –{" "}
                {formatDayKey(rotationInfo.donemBitis)} ({rotationInfo.ad})
              </>
            ) : null}
          </Muted>
          <WeeklyShiftTable
            days={rotationShift.days}
            shiftName={rotationShift.code}
            color={rotationShift.color}
          />
          <DayList>
            {DAYS_OF_WEEK.map((d) => {
              const day = rotationShift.days?.find(
                (x: any) => x.dayOfWeek === d.value
              );
              return (
                <DayRow key={d.value}>
                  <DayName>{d.label}</DayName>
                  <span>{day ? dayRangeLabel(day) : "Çalışılmıyor"}</span>
                </DayRow>
              );
            })}
          </DayList>
          <Muted style={{ marginTop: 12 }}>
            Bu program ekibin rotasından hesaplanıyor; dönem değişince
            kendiliğinden değişir. Buradan ayrıca vardiya atamak{" "}
            <strong>gerekmez</strong> — atarsanız ekip rotasının üstüne biner.
          </Muted>
        </>
      ) : currentTeam && rotationShift === null ? (
        /* Rota bu dönem ekibe vardiya vermiyor: dinlenme dönemi. */
        <TeamNote>
          <strong>{currentTeam.code} · {currentTeam.name}</strong> ekibi bu dönem{" "}
          <strong>dinleniyor</strong> — rotada bu döneme vardiya atanmamış.
          {rotationInfo ? (
            <>
              <br />
              Dönem: {formatDayKey(rotationInfo.donemBaslangic)} –{" "}
              {formatDayKey(rotationInfo.donemBitis)} ({rotationInfo.ad})
            </>
          ) : null}
        </TeamNote>
      ) : currentTeam && rotationLoading ? (
        <Muted>Ekibin rotası okunuyor…</Muted>
      ) : currentTeam ? (
        /*
         * ⚠ BU DAL BİR HATAYI KAPATIYOR. Önceden ekip ataması olan personelde de
         * "Program göstermek için önce bir vardiya atanmalı" yazıyordu — oysa
         * kişinin vardiyası VAR, ekibinin rotasından geliyor. Mesaj İK'yı
         * doğrudan ikinci bir atama yapmaya itiyordu; o atama da ekip
         * atamasının üstüne biner ve rotayı bozar.
         *
         * Haftalık program burada hesaplanmıyor: ekibin o haftaki vardiyası
         * rota matrisinden çıkıyor ve o matris "Bu Hafta Kim Nerede" ekranının
         * işi (bkz. yukarıdaki not). Burada yapılan tek şey doğruyu söylemek.
         */
        /*
         * Buraya yalnızca ekip HİÇBİR ROTADA değilse düşülür. Rota varsa
         * yukarıdaki iki dal (vardiya çizilir / dinlenme) çalışır.
         */
        <TeamNote>
          <strong>{currentTeam.code} · {currentTeam.name}</strong> ekibinde, ama
          bu ekip <strong>hiçbir rotaya bağlı değil</strong> — bu yüzden haftalık
          programı hesaplanamıyor.
          <br />
          Ekibi bir çevrime bağlamak için{" "}
          <TeamNoteLink to="/shifts/rotations">
            Vardiyalar → Rotalar
          </TeamNoteLink>{" "}
          ekranından rota kurun. Bunun yerine buradan doğrudan vardiya atamak da
          mümkün, ama sonradan rota kurulursa atamanız rotanın üstüne biner.
        </TeamNote>
      ) : (
        <Muted>Program göstermek için önce bir vardiya atanmalı.</Muted>
      )}

      <Modal
        centered
        size="xl"
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Vardiya Ata / Kaldır"
      >
        <Flex direction="column" gap={40}>
          <Flex align="flex-end" gap={20} wrap="wrap">
            <CustomSelect
              w={220}
              h={44}
              label="Vardiya"
              placeholder="Vardiya Seçiniz"
              data={shiftOptions}
              value={selectedShift}
              onChange={setSelectedShift}
              searchable
            />
            <CustomDatePicker
              w={200}
              label="Başlangıç Tarihi"
              placeholder="Tarih Seçiniz"
              value={startDate}
              onChange={setStartDate}
            />
            <CustomDatePicker
              w={200}
              disabled={!startDate}
              minDate={startDate ?? undefined}
              label="Bitiş Tarihi (Opsiyonel)"
              placeholder="Süresiz"
              value={endDate}
              onChange={setEndDate}
            />
            <Button color="#5c26ff" loading={saving} onClick={() => submit(false)}>
              Ekle
            </Button>
          </Flex>

          {/*
            `length > 0 &&` idi: hiç ataması olmayan personelde kipin alt yarısı
            bomboş kalıyordu. DataTable kendi boş durumunu zaten basıyor, o
            yüzden koşulu kaldırmak hem daha az kod hem daha doğru.
          */}
          <DataTable
            columns={assignmentColumns}
            rows={assignments}
            rowKey={(row: any) => row._id}
            emptyText="Bu personel için kayıtlı atama yok."
          />
        </Flex>
      </Modal>

      <ShiftConflictErrorModal
        opened={conflictModalOpen}
        setOpened={setConflictModalOpen}
        conflicts={conflicts}
        onCloseAndContinue={() => submit(true)}
      />
    </div>
  );
};

export default EmployeeShiftDetails;

const TeamNote = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 16px;
  color: #4b5563;
  font-size: 14px;
  line-height: 22px;
  max-width: 720px;
`;

const TeamNoteLink = styled(Link)`
  color: #5c26ff;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: black;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 16px;
`;

const Muted = styled.div`
  color: #6b7280;
  font-size: 14px;
`;

const DayList = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px 24px;
`;

const DayRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #4b5563;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 6px;
`;

const DayName = styled.span`
  font-weight: 500;
  color: #111827;
`;
